<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin">
      <q-card-section>
        <div class="text-h6">Экспорт</div>
      </q-card-section>
      <q-card-section>
        <q-input v-model="name" label="Название" :rules="[requiredRule]"></q-input>
        <q-select :loading="isFetching" label="Шаблон" v-model="templateAddress" :options="templates"
                  option-value="address" option-label="name" emit-value map-options></q-select>
      </q-card-section>
      <!--
        ...content
        ... use q-card-section for it?
      -->

      <!-- buttons example -->
      <q-card-actions align="right">
        <q-btn color="primary" label="OK" :loading="loading" :disable="!name" @click="exportTasks"/>
        <q-btn color="primary" label="Отмена" :disable="loading" @click="onDialogCancel"/>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import {useDialogPluginComponent} from 'quasar'
import {
  useCreateProjectTemplate,
  useProjectPhotoCaption,
  useProjectTemplate,
  useProjectTemplates
} from "../composables/project";
import {useQuasar} from "quasar";
import createReport from 'docx-templates';
import {saveAs} from 'file-saver';
import {useTasksReports} from "../composables/task";
import {useRouteParams} from "@vueuse/router";
import {ref} from "vue";
import {Photo, ReportPhoto} from "../models/task";
import {newCdnFetch, useCdnFetch} from "../composables/fetch";
import {ReportPerformTask} from '../models/performTask';
import {useDayJs} from '../composables/dayjs';
import {Dayjs} from 'dayjs';
import {requiredRule} from '../util/rules';
import * as Sentry from "@sentry/vue";
import {useQuery, useQueryClient} from "@tanstack/vue-query";
import {buildPhotoCdnPath} from "../util/photoCdn";

const props = defineProps<{
  tasks: number[]
}>()

const dayjs = useDayJs()

defineEmits([
  // REQUIRED; need to specify some events that your
  // component will emit through useDialogPluginComponent()
  ...useDialogPluginComponent.emits
])
const queryClient = useQueryClient()

const {dialogRef, onDialogHide, onDialogOK, onDialogCancel} = useDialogPluginComponent()

const projectId = useRouteParams('projectId', undefined, {transform: Number})
const {isFetching, data: templates, execute} = useProjectTemplates(projectId)

const templateAddress = ref<string | null>(null)
const loading = ref<boolean>(false)
const name = ref<string>("")
const progress = ref<number>(0)
const $q = useQuasar()

async function imageSize(buffer: ArrayBuffer): Promise<[number, number]> {
  return new Promise((resolve, reject) => {
    let img = new Image()
    const blob = new Blob([buffer], {type: 'image/jpeg'})
    const object = URL.createObjectURL(blob)
    img.onload = () => {
      const result: [number, number] = [img.width, img.height]
      URL.revokeObjectURL(object)
      resolve(result)
    }
    img.onerror = reject
    img.src = object
  })
}

async function exportTasks() {
  progress.value = 0
  loading.value = true
  window.Buffer = window.ArrayBuffer
  if (templateAddress.value == null) {
    return;
  }
  const {data} = await useProjectTemplate(projectId, templateAddress.value)
  const {data: report} = await useTasksReports(projectId, props.tasks);
  try {
    const buffer = await createReport({
      template: data.value,
      additionalJsContext: {
        photoImage: async (photo: ReportPhoto, width: [number, number] = [6.35, 8.38], height: [number, number] = [11.28, 6], fontSize: number = 16) => {
          const updatedWidth = width;
          const updatedHeight = height
          const url = buildPhotoCdnPath(photo.address, {
            preview: '1280x960',
            formatBeforeCaption: 'jpeg',
            caption: photo.caption ? {text: photo.caption, fontSize} : null,
          })
          Sentry.addBreadcrumb({
            category: 'photoImage',
            message: url
          })
          const image = await queryClient.fetchQuery<ArrayBuffer>({
            queryKey: ['photoImage', url],
            queryFn: () => newCdnFetch.url(encodeURI(url))
                .get()
                .arrayBuffer(),
            retry: 10,
          })
          if (!image) {
            return null
          }
          const [photoWidth, photoHeight] = await imageSize(image)
          if (photoWidth > photoHeight) {
            const url = buildPhotoCdnPath(photo.address, {
              preview: '960x960',
              formatBeforeCaption: 'jpeg',
              caption: photo.caption ? {text: photo.caption, fontSize: fontSize + 2} : null,
            })
            const image = await queryClient.fetchQuery<ArrayBuffer>({
              queryKey: ['photoImage', url],
              queryFn: () => newCdnFetch.url(encodeURI(url))
                  .get()
                  .arrayBuffer(),
              retry: 10,
            })
            return {
              width: updatedWidth[1],
              height: updatedHeight[1],
              data: image,
              extension: '.jpeg'
            };
          }
          return {
            width: photoWidth > photoHeight ? updatedWidth[1] : updatedWidth[0],
            height: photoWidth > photoHeight ? updatedHeight[1] : updatedHeight[0],
            data: image,
            extension: '.jpeg'
          };
        },
        extract: (name: string, regex: RegExp) => {
          const result = name.match(regex)
          if (!result || result.length == 0) {
            return name
          } else {
            return result[0]
          }
        },
        loadTask: () => {
          progress.value += 1
        },
        dateEnd: (tasks: ReportPerformTask[]) => {
          return tasks.map(t => dayjs.utc(t.executionTime)).sort((a, b) => b.diff(a))[0]
        },
        dateFormat: (date: string | Dayjs, format: string) => {
          return dayjs.utc(date).local().format(format)
        },
        dateStart: (tasks: ReportPerformTask[]) => {
          return tasks.map(t => dayjs.utc(t.executionTime)).sort((a, b) => a.diff(b))[0]
        },
        chunk: (array: unknown[], chunk: number) => {
          const reversedArray = array
          let result: unknown[] = reversedArray.length > 0 ? [] : [[null, null, null]];
          for (let i = 0; i < reversedArray.length; i += chunk) {
            result.push(reversedArray.slice(i, i + chunk));
          }
          return result;
        },
        chunkDate: (array: ReportPhoto[], chunkSize: number) => {
          // Create a reversed copy to avoid mutating the original array
          const reversedArray = [...array]
          const dateGroups: { [key: string]: ReportPhoto[] } = {};

          // Group photos by their createdAt date
          for (const photo of reversedArray) {
            const dateKey = dayjs.utc(photo.createdAt).local().format('YYYY-MM-DD');
            if (!dateGroups[dateKey]) {
              dateGroups[dateKey] = [];
            }
            dateGroups[dateKey].push(photo);
          }

          const result: ReportPhoto[][] = [];

          // Split each date group into chunks of specified size
          for (const dateKey of Object.keys(dateGroups)) {
            const group = dateGroups[dateKey];
            for (let i = 0; i < group.length; i += chunkSize) {
              result.push(group.slice(i, i + chunkSize));
            }
          }

          // Handle empty array case with null placeholder
          if (result.length === 0) {
            return [[null!, null!, null!]]; // Using non-null assertion to match original behavior
          }

          return result;
        }
      },
      noSandbox: true,
      data: {...report.value, name: name.value},
    });
    saveAs(new Blob([buffer]), `${name.value.replace(" ", "_")}.docx`)
    progress.value = report.value?.length ?? 0
    loading.value = false
    onDialogOK()
  } catch (e) {
    Sentry.captureException(e)
    $q.notify({
      type: 'negative',
      message: String(e)
    })
  }
  progress.value = report.value?.length ?? 0
  loading.value = false
  onDialogOK()
}
</script>

<style scoped></style>
